import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import { ROILineChartHeader } from './ROILineChartHeader';
var ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, annotationPlugin);
var SHADED_BOUNDS = {
    upper: [28000, 371000, 717000, 1418000, 2120000],
    lower: [-45000, 224000, 489000, 1013000, 1537000],
};
var Y_AXIS_MIN = -400000;
var Y_AXIS_MAX = 5000000;
export var ROILineChart = function (_a) {
    var _b, _c, _d, _e, _f;
    var roiData = _a.roiData, title = _a.title;
    if ((roiData === null || roiData === void 0 ? void 0 : roiData.roi_15_year) == null)
        return null;
    var dataPoints = [
        { year: 10, value: (_b = roiData.roi_10_year) !== null && _b !== void 0 ? _b : null },
        { year: 15, value: (_c = roiData.roi_15_year) !== null && _c !== void 0 ? _c : null },
        { year: 20, value: (_d = roiData.roi_20_year) !== null && _d !== void 0 ? _d : null },
        { year: 30, value: (_e = roiData.roi_30_year) !== null && _e !== void 0 ? _e : null },
        { year: 40, value: (_f = roiData.roi_40_year) !== null && _f !== void 0 ? _f : null },
    ];
    var labels = dataPoints.map(function (p) { return "".concat(p.year, " Years"); });
    var values = dataPoints.map(function (p) { return p.value; });
    var data = {
        labels: labels,
        datasets: [
            {
                label: 'upper',
                data: SHADED_BOUNDS.upper,
                borderColor: 'transparent',
                backgroundColor: 'transparent',
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0,
                fill: false,
                order: 2,
            },
            {
                label: 'lower',
                data: SHADED_BOUNDS.lower,
                borderColor: 'transparent',
                backgroundColor: COLOR_TOKENS['Brand/100'],
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0,
                fill: '-1',
                order: 2,
            },
            {
                label: title,
                data: values,
                borderColor: COLOR_TOKENS['Brand/500'],
                tension: 0,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: COLOR_TOKENS['Brand/500'],
                fill: false,
                order: 0,
            },
        ],
    };
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                title: {
                    display: true,
                    text: ROI_LINE_CHART_STRINGS.yAxisLabel,
                    color: COLOR_TOKENS['Colors/Text/text-tertiary'],
                    font: {
                        size: 12,
                        family: 'Sarabun, sans-serif',
                    },
                },
                grid: {
                    tickColor: 'transparent',
                    display: true,
                    color: function (context) {
                        var value = context.tick.value;
                        if (value === 0) {
                            return COLOR_TOKENS['Gray/300'];
                        }
                        return COLOR_TOKENS['Colors/Border/border-tertiary'];
                    },
                },
                border: {
                    display: false,
                },
                min: Y_AXIS_MIN,
                max: Y_AXIS_MAX,
                afterBuildTicks: function (axis) {
                    axis.ticks = [Y_AXIS_MIN, 0, 500000, 1000000, 2000000, 3000000, 4000000, Y_AXIS_MAX].map(function (value) { return ({
                        value: value,
                    }); });
                },
                ticks: {
                    callback: function (value) {
                        var valueAsNumber = Number(value);
                        if (valueAsNumber === Y_AXIS_MIN)
                            return '';
                        if (valueAsNumber === 0)
                            return '$0 ';
                        var absValue = Math.abs(valueAsNumber);
                        var sign = valueAsNumber < 0 ? '-' : '';
                        if (absValue < 1000000) {
                            return "".concat(sign, "$").concat(absValue / 1000, "k ");
                        }
                        return "".concat(sign, "$").concat(absValue / 1000000, "M ");
                    },
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                },
            },
            x: {
                title: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    align: 'center',
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                },
            },
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
        },
        plugins: {
            tooltip: {
                enabled: true,
                displayColors: false,
                padding: {
                    top: 8,
                    bottom: 8,
                    left: 12,
                    right: 12,
                },
                caretSize: 0,
                yAlign: 'bottom',
                filter: function (tooltipItem) {
                    return tooltipItem.dataset.label !== 'upper' && tooltipItem.dataset.label !== 'lower';
                },
                callbacks: {
                    title: function (context) {
                        var _a, _b;
                        return (_b = (_a = context[0]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '';
                    },
                    label: function (context) {
                        var value = context.parsed.y;
                        return "ROI: ".concat(formatCurrency(value));
                    },
                },
            },
            legend: {
                labels: {
                    generateLabels: function () { return []; },
                },
            },
            title: {
                display: false,
            },
        },
        hover: {
            mode: 'index',
            intersect: false,
        },
    };
    return (_jsxs(Box, { sx: containerSx, children: [_jsx(ROILineChartHeader, { total_roi: roiData.roi_15_year }), _jsx(Box, { sx: chartContainerSx, children: _jsx(Line, { data: data, options: options }) }), _jsxs(Box, { sx: legendContainerSx, children: [_jsx(Box, { sx: legendColorBoxSx }), _jsx(Typography, { variant: "text-sm-regular", sx: legendTextSx, children: ROI_LINE_CHART_STRINGS.legendLabel })] })] }));
};
var chartContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        height: '100%',
        mt: backrs.spacing['spacing-xl'],
    });
};
var containerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 768px)': {
            width: '100%',
        },
        '@media (max-width: 400px)': {
            width: '100%',
        },
        height: { xs: 'auto', lg: '405px' },
        backgroundColor: backrs.colors['Colors/Base/White'],
        border: "1px solid ".concat(backrs.colors['Colors/Foreground/fg-primary']),
        boxShadow: backrs.shadows['shadow-xs'],
        borderRadius: backrs.radius['radius-xl'],
        padding: {
            xs: backrs.spacing['spacing-xl'],
            sm: backrs.spacing['spacing-2xl'],
        },
    });
};
var legendContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-sm'],
        mt: backrs.spacing['spacing-lg'],
    });
};
var legendColorBoxSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '16px',
        height: '16px',
        backgroundColor: COLOR_TOKENS['Brand/100'],
        borderRadius: backrs.radius['radius-xs'],
        flexShrink: 0,
    });
};
var legendTextSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-tertiary'],
    });
};
